import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

export const SherpaDisclaimerView = ({
  onBegin,
  isLoading,
  buttonLabel = "Begin talking to Sherpa",
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 720,
        mx: "auto",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
        overflowY: "auto",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: "#154C3A",
          mb: 2.5,
          fontFamily: "'Work Sans', 'Plus Jakarta Sans', sans-serif",
        }}
      >
        Terms of Use / Disclaimer
      </Typography>

      <Stack spacing={2.5}>
        {/* 1. Security & Privacy Card */}
        <Card
          variant="outlined"
          sx={{
            borderRadius: "16px",
            borderColor: "rgba(226, 236, 232, 0.9)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
            backgroundColor: "#FFFFFF",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#F3FBF9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: "#0E8B63",
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#154C3A",
                    mb: 0.5,
                  }}
                >
                  Your conversations are secure and protected.
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#5C7268",
                    lineHeight: 1.6,
                  }}
                >
                  Sherpa securely stores chats to enhance your experience while
                  safeguarding your privacy. Your data is processed in an
                  aggregated format to prevent identification, and your employer
                  has no access to your conversations.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* 2. AI Limitations Card */}
        <Card
          variant="outlined"
          sx={{
            borderRadius: "16px",
            borderColor: "rgba(226, 236, 232, 0.9)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
            backgroundColor: "#FFFFFF",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#F3FBF9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: "#0E8B63",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#154C3A",
                    mb: 0.5,
                  }}
                >
                  AI limitations and professional boundaries.
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#5C7268",
                    lineHeight: 1.6,
                    mb: 1,
                  }}
                >
                  This AI coach is designed to provide helpful guidance and
                  engage in wellness conversations. However, it is an
                  artificial intelligence and its responses are based on the
                  data it has been trained on.
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#5C7268",
                    lineHeight: 1.6,
                    mb: 1,
                  }}
                >
                  The information provided should not be considered professional
                  medical or psychiatric advice. For specific advice tailored to
                  your situation, please consult with qualified healthcare
                  professionals.
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#5C7268",
                    lineHeight: 1.6,
                  }}
                >
                  We are not responsible for any independent actions taken
                  based on the information provided by the chatbot.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* 3. Emergency Support Notice */}
        <Card
          variant="outlined"
          sx={{
            borderRadius: "16px",
            borderColor: "#FFE4B5",
            backgroundColor: "#FFFDF8",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#FFF3E0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: "#E65100",
                }}
              >
                <WarningAmberOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#A04000",
                    mb: 0.5,
                  }}
                >
                  Emergency Assistance
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#7E5109",
                    lineHeight: 1.6,
                  }}
                >
                  Neither Mindsherpa nor any of our services provide emergency
                  medical or crisis services. If you are experiencing a medical
                  emergency or urgent mental health crisis, please call 911 or
                  contact the 988 Suicide &amp; Crisis Lifeline immediately.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* 4. Action Button */}
        <Box sx={{ pt: 1, pb: 4 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            onClick={onBegin}
            sx={{
              backgroundColor: "#0E8B63",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: 15,
              py: 1.75,
              borderRadius: "12px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#0B6B4C",
                boxShadow: "0 4px 12px rgba(14, 139, 99, 0.25)",
              },
            }}
          >
            {isLoading ? "Starting Session..." : buttonLabel}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default SherpaDisclaimerView;
